import { TFunction } from 'i18next'
import { downloadBlob } from '@/shared/utils/downloadBlob'
import { formatPrice } from '@/shared/utils/price'
import { Shift } from '../types/schedule.model'
import { DateRange, PayrollRow, isSunday, shiftsInRange } from './payroll'
import { formatMinutes, shiftDurationHours } from './schedule'

/**
 * CSV rather than XLSX: the schedule has no backend, so a report has to be
 * produced in the browser, and CSV is the one format that needs no dependency
 * and opens in every spreadsheet.
 */

/** Semicolon-separated. Excel in a Polish locale reads commas as decimals. */
const SEPARATOR = ';'

const escapeCell = (value: string | number): string => {
  const text = String(value)
  return /[";\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const toCsv = (rows: Array<Array<string | number>>): string =>
  rows.map(row => row.map(escapeCell).join(SEPARATOR)).join('\n')

/** Decimal comma, so the numbers land in cells rather than as text. */
const hours = (value: number): string => value.toFixed(2).replace('.', ',')

export const payrollCsv = (rows: PayrollRow[], t: TFunction): string =>
  toCsv([
    [
      t('SCHEDULE.EMPLOYEE'),
      t('PAYROLL.HOURS_TO_DATE'),
      t('PAYROLL.HOURS_SCHEDULED'),
      t('PAYROLL.OVERTIME'),
      t('PAYROLL.NIGHT'),
      t('PAYROLL.SUNDAY'),
      t('PAYROLL.PAY_TO_DATE'),
      t('PAYROLL.PAY_ESTIMATED')
    ],
    ...rows.map(row => [
      row.name,
      hours(row.hoursToDate),
      hours(row.hoursScheduled),
      hours(row.overtimeHours),
      hours(row.nightHours),
      hours(row.sundayHours),
      row.payToDateMinorUnits === undefined
        ? ''
        : formatPrice(row.payToDateMinorUnits, { withCurrency: false }),
      row.payEstimatedMinorUnits === undefined
        ? ''
        : formatPrice(row.payEstimatedMinorUnits, { withCurrency: false })
    ])
  ])

export const rosterCsv = (
  employees: Array<{ userId: string; name: string }>,
  shifts: Shift[],
  range: DateRange,
  t: TFunction
): string => {
  const nameOf = (userId: string) =>
    employees.find(employee => employee.userId === userId)?.name ?? userId

  const rangeShifts = shiftsInRange(shifts, range).sort(
    (a, b) => a.date.localeCompare(b.date) || a.startMinutes - b.startMinutes
  )

  return toCsv([
    [
      t('SCHEDULE.EXPORT_MODAL.COLUMN_DATE'),
      t('SCHEDULE.EMPLOYEE'),
      t('SCHEDULE.MODAL.START'),
      t('SCHEDULE.MODAL.END'),
      t('PAYROLL.HOURS_SCHEDULED'),
      t('PAYROLL.SUNDAY'),
      t('SCHEDULE.MODAL.NOTE')
    ],
    ...rangeShifts.map(shift => [
      shift.date,
      nameOf(shift.userId),
      formatMinutes(shift.startMinutes),
      formatMinutes(shift.endMinutes),
      hours(shiftDurationHours(shift)),
      isSunday(shift.date) ? t('COMMON.YES') : '',
      shift.note ?? ''
    ])
  ])
}

/** Byte-order mark, without it Excel reads UTF-8 as Latin-1 and mangles ą/ł/ż. */
const UTF8_BOM = '﻿'

export const downloadCsv = (fileName: string, content: string): void => {
  const blob = new Blob([`${UTF8_BOM}${content}`], {
    type: 'text/csv;charset=utf-8;'
  })
  downloadBlob(blob, fileName)
}
